import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Flex, Text } from "styles/utility-components";

import { toast } from "components/ui/toast/Toast";
import Toggle from "components/ui/toggle/Toggle";
import Tooltip from "components/ui/tooltip/Tooltip";
import { useSession } from "lib/auth/authClient";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { betterAuthSessionQueryOptions } from "lib/hooks/api/auth/betterAuthSessionQuery";
import { useCompanyAuthConfig } from "lib/hooks/api/company/useCompanyAuthConfig";
import { useUpdateCompany } from "lib/hooks/api/company/useUpdateCompany";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import SettingsContentContainer from "../SettingsContentContainer";
import TurnOffMFA from "./TurnOffMFA";
import TurnOnMFA from "./TurnOnMFA";
import { MfaSetup } from "./utils";
import { VerifyMFA } from "./VerifyMFA";

// Per-user TOTP enrolment. Drives the better-auth enable → verify → enabled flow, and disable. When
// the company mandates 2FA the user can still enrol but can't turn it off.
function PersonalTwoFactor() {
  const { data: session, isPending, refetch: refetchSession } = useSession();
  const { data: authConfig } = useCompanyAuthConfig();
  const queryClient = useQueryClient();

  // Set once `enable` returns; holds the QR URI + backup codes while the user verifies a code.
  const [setup, setSetup] = useState<MfaSetup | null>(null);

  // Run after enrol/disable: refresh better-auth's own session (drives this page's enabled state)
  // AND invalidate the root guard's cached session, which never auto-refetches (staleTime: Infinity).
  // Without the invalidation, a user who just enrolled would keep being redirected to 2FA setup.
  const onTwoFactorChange = async () => {
    await Promise.all([
      refetchSession({ query: { disableCookieCache: true } }),
      queryClient.invalidateQueries({ queryKey: betterAuthSessionQueryOptions().queryKey }),
    ]);
  };

  if (isPending) return <CentredSpinner />;

  // 2FA management runs against the better-auth session. A Rails session can outlive it, so point
  // those users to the sign-in page rather than showing a broken form.
  if (!session?.user) {
    return (
      <Flex gap={8} flexDirection="column">
        <Text color="sysTextSecondary">Log in to manage two-factor authentication.</Text>
        <Link to="/auth/login">Go to log in</Link>
      </Flex>
    );
  }

  const isEnabled = Boolean(session.user.twoFactorEnabled);
  const companyRequires2FA = Boolean(authConfig?.requireTwoFactor);

  if (isEnabled) {
    return <TurnOffMFA companyRequires2FA={companyRequires2FA} onSuccess={onTwoFactorChange} />;
  }

  // Mid-enrolment: show the QR + backup codes and ask for a verification code.
  if (setup) {
    return <VerifyMFA setup={setup} setSetup={setSetup} onSuccess={onTwoFactorChange} />;
  }

  return <TurnOnMFA companyRequires2FA={companyRequires2FA} setSetup={setSetup} />;
}

// Company-wide auth settings. Only company admins can change these (also enforced server-side). The
// company guid comes from currentUser since useCompanyAuthConfig only returns the flags.
function CompanyPolicy() {
  const { data: currentUser } = useCurrentUser();
  const { data: session } = useSession();
  const { data: authConfig, refetch } = useCompanyAuthConfig();
  const { mutate: updateCompany, isLoading } = useUpdateCompany({
    onSuccess: () => {
      void refetch();
    },
    onError: () => toast.error("Could not update company authentication settings"),
  });

  const isAdmin = Boolean(currentUser?.isCompanyAdmin || currentUser?.isAdmin);
  const companyGuid = currentUser?.company.guid;
  if (!isAdmin || !companyGuid || !authConfig) return null;

  const isRequired = Boolean(authConfig.requireTwoFactor);
  // Admins must be enrolled themselves before mandating 2FA; turning the requirement off is always allowed.
  const blockedUntilEnrolled = !isRequired && !session?.user?.twoFactorEnabled;
  const mcpCredentialsAdminOnly = authConfig.restrictMcpCredentialsToAdmins;

  return (
    <Flex gap={16} flexDirection="column">
      <Text h3>Company policy</Text>
      <Flex gap={8} alignItems="center">
        <Tooltip
          content={
            blockedUntilEnrolled
              ? "Set up two-factor authentication on your own account before requiring it for everyone."
              : undefined
          }
        >
          {/* Wrapper so the tooltip still triggers while the toggle inside is disabled */}
          <Flex>
            <Toggle
              aria-label="Require two-factor authentication for everyone"
              checked={isRequired}
              disabled={isLoading || blockedUntilEnrolled}
              onCheckedChange={() =>
                updateCompany({
                  input: {
                    guid: companyGuid,
                    authConfig: {
                      requireTwoFactor: !isRequired,
                    },
                  },
                })
              }
            />
          </Flex>
        </Tooltip>
        <Text>Require all members to use two-factor authentication</Text>
      </Flex>
      <Text description color="sysTextSecondary">
        When on, anyone in your organisation without 2FA will be asked to set it up the next time
        they open or reload Stotles.
        {blockedUntilEnrolled && " Set up 2FA on your own account first."}
      </Text>
      <Flex gap={8} alignItems="center">
        <Toggle
          aria-label="Only let company admins manage MCP credentials"
          checked={mcpCredentialsAdminOnly}
          disabled={isLoading}
          onCheckedChange={() =>
            updateCompany({
              input: {
                guid: companyGuid,
                authConfig: {
                  restrictMcpCredentialsToAdmins: !mcpCredentialsAdminOnly,
                },
              },
            })
          }
        />
        <Text>Only let company admins manage MCP credentials</Text>
      </Flex>
      <Text description color="sysTextSecondary">
        When on, only company admins can manage MCP credentials: the client IDs, secrets and
        callback URLs that tools such as Copilot Studio and Gemini Enterprise connect with. Members
        can still see them, sign in to tools set up with them, and connect tools that register
        themselves, such as Claude and ChatGPT.
      </Text>
    </Flex>
  );
}

function TwoFactorSettings(): JSX.Element {
  return (
    <SettingsContentContainer title="Security">
      <Flex gap={16} flexDirection="column">
        <PersonalTwoFactor />
        <CompanyPolicy />
      </Flex>
    </SettingsContentContainer>
  );
}

export default TwoFactorSettings;
